import { gql, useQuery } from "@apollo/client";
import { Fade } from "@material-ui/core";
import { useCurrentQueryParamStateWithDefaults } from "components/Table/hooks/useQueryParamState";
import {
  AllOrganizationContentTemplateIds,
  AllOrganizationContentTemplateIdsVariables,
  ContentTemplatesPaginated,
  ContentTemplatesPaginatedVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { DefaultContentTemplatesStatusFilter } from "utils/constants/defaults";
import { useInitialQueryStringValue } from "utils/next";

import ContentTemplatesTable from "./ContentTemplatesTable";
import CreateContentTemplateModal, {
  ContentTemplateToDuplicate,
} from "./CreateContentTemplateModal";

const CONTENT_TEMPLATES = gql`
  query ContentTemplatesPaginated(
    $search: String
    $offset: Int
    $limit: Int
    $status: ContentTemplateStatusEnum
    $orderBy: [OrganizationContentTemplatesOrderByInput!]
  ) {
    currentUserPrisma {
      id
      currentOrganization {
        id
        contentTemplates(
          where: { search: $search, status: $status }
          orderBy: $orderBy
          limit: $limit
          offset: $offset
        ) {
          id
          name
          createdAt
          updatedAt
          archivedAt
          status
          creator {
            id
            name: fullName
            imageUrl
          }
        }
      }
    }
  }
`;

const ALL_CONTENT_TEMPLATES_IDS = gql`
  query AllOrganizationContentTemplateIds(
    $search: String
    $status: ContentTemplateStatusEnum
  ) {
    currentUserPrisma {
      id
      currentOrganization {
        id
        contentTemplates(where: { search: $search, status: $status }) {
          id
        }
      }
    }
  }
`;

function ContentTemplates() {
  const router = useRouter();
  const { initialValue: initialNew } = useInitialQueryStringValue("new");
  const createNew = initialNew === "true";
  const [open, setOpen] = useState(createNew);

  const [contentTemplateToDuplicate, setContentTemplateToDuplicate] = useState<
    ContentTemplateToDuplicate | undefined
  >();
  const {
    pageIndex,
    pageSize,
    filters,
    globalFilter: search,
    sortBy,
  } = useCurrentQueryParamStateWithDefaults();
  const initialVariables = {
    limit: pageSize,
    offset: pageIndex * pageSize,
    search,
    status:
      _.find(filters, { id: "status" })?.value ??
      DefaultContentTemplatesStatusFilter,
    orderBy: _(sortBy)
      .map(({ id, desc }) => {
        let direction;
        if (id === "createdAt") {
          direction = desc ? "asc" : "desc";
        } else {
          direction = desc ? "desc" : "asc";
        }
        return {
          [id]: direction,
        };
      })
      .value(),
  };

  const {
    data,
    loading: isLoadingContentTemplates,
    refetch: refetchPage,
  } = useQuery<ContentTemplatesPaginated, ContentTemplatesPaginatedVariables>(
    CONTENT_TEMPLATES,
    {
      variables: initialVariables,
      fetchPolicy: "cache-and-network",
    }
  );

  const {
    data: allContentTemplateIdsData,
    loading: isLoadingAllContentTemplateIds,
    refetch: refetchAllIds,
  } = useQuery<
    AllOrganizationContentTemplateIds,
    AllOrganizationContentTemplateIdsVariables
  >(ALL_CONTENT_TEMPLATES_IDS, {
    variables: initialVariables,
    fetchPolicy: "cache-and-network",
  });

  const refetch = () => {
    refetchPage();
    refetchAllIds();
  };

  const contentTemplates =
    data?.currentUserPrisma?.currentOrganization?.contentTemplates || [];
  const allContentTemplateIds =
    _.map(
      allContentTemplateIdsData?.currentUserPrisma?.currentOrganization
        ?.contentTemplates,
      "id"
    ) || [];
  const totalCount = allContentTemplateIds.length;
  const isLoading = isLoadingContentTemplates || isLoadingAllContentTemplateIds;

  const refetchIds = useCallback(
    async ({ search: newSearch }) => {
      refetchAllIds({
        search: newSearch,
      });
    },
    [refetchAllIds]
  );

  return (
    <Fade in timeout={1000}>
      <div className="mt-8">
        <ContentTemplatesTable
          loading={isLoading}
          initialVariables={initialVariables}
          refetchIds={refetchIds}
          contentTemplates={contentTemplates}
          allContentTemplateIds={allContentTemplateIds}
          totalContentTemplatesCount={totalCount}
          refetchData={refetch}
          onCreateContentTemplate={(
            newContentTemplateToDuplicate: ContentTemplateToDuplicate
          ) => {
            setContentTemplateToDuplicate(newContentTemplateToDuplicate);
            setOpen(true);
          }}
        />
        <CreateContentTemplateModal
          contentTemplateToDuplicate={contentTemplateToDuplicate}
          open={open}
          setOpen={() => {
            setContentTemplateToDuplicate(undefined);
            setOpen(false);
          }}
          afterSubmit={(newContentTemplateId) => {
            refetch();
            router.push(`/templates/content/${newContentTemplateId}`);
          }}
        />
      </div>
    </Fade>
  );
}

export default ContentTemplates;
